import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Add, Search } from '@mui/icons-material'
import { Box, Button, Card, InputAdornment, Stack, TableContainer, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetLanguageMapsQuery } from '../../api/language'
import { useDeleteTagGroupMutation, useGetInternalTagGroupsQuery, useSaveTagGroupMutation, useSaveTagGroupOrderMutation } from '../../api/tag-groups'
import { useGetInternalTagsQuery } from '../../api/tags'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { TagGroupEditDialog } from '../../features/tag-groups/components/TagGroupEditDialog/TagGroupEditDialog'
import {
  buildTagGroupRows,
  filterTagGroupRows,
  getUnstoredTagGroupFields,
  tagGroupFieldLabelKeys,
  tagGroupTypeLabelKeys,
  tagGroupTypes,
  TagGroupRow,
  toTagGroupOrder,
} from '../../features/tag-groups/helpers/tagGroupHelpers'
import { TagGroupMutation, TagGroupType } from '../../features/tag-groups/types/InternalTagGroup'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalTagType } from '../../types/InternalTag'

type CustomProps = {
  onEdit: (row: TagGroupRow) => void
  onDelete: (row: TagGroupRow) => void
}

type DialogState = { open: boolean; row?: TagGroupRow }

type PageConfirmData = { action: 'delete'; row: TagGroupRow } | { action: 'discardOrder'; type: TagGroupType }

const isTagGroupType = (value: string | null): value is TagGroupType => tagGroupTypes.includes(value as TagGroupType)

export const TagGroupsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-tag-groups'))
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const typeParam = searchParams.get('type')
  const type: TagGroupType = isTagGroupType(typeParam) ? typeParam : InternalTagType.Feature

  const tagGroupsQuery = useGetInternalTagGroupsQuery()
  const tagsQuery = useGetInternalTagsQuery()
  const languageMapsQuery = useGetLanguageMapsQuery()
  const [saveTagGroup, { isLoading: isSaving }] = useSaveTagGroupMutation()
  const [deleteTagGroup] = useDeleteTagGroupMutation()
  const [saveTagGroupOrder, { isLoading: isSavingOrder }] = useSaveTagGroupOrderMutation()

  const [search, setSearch] = useState('')
  const [reorderedIds, setReorderedIds] = useState<string[]>()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<PageConfirmData>>()

  const tags = useMemo(() => tagsQuery.data ?? [], [tagsQuery.data])
  const storedRows = useMemo(
    () => buildTagGroupRows(tagGroupsQuery.data ?? [], languageMapsQuery.data, tags, type),
    [tagGroupsQuery.data, languageMapsQuery.data, tags, type]
  )
  const orderedRows = useMemo(() => {
    if (!reorderedIds) return storedRows
    const rowsById = new Map(storedRows.map((row) => [row.tagGroup.id, row]))
    const reordered = reorderedIds.flatMap((id) => rowsById.get(id) ?? [])
    return [...reordered, ...storedRows.filter((row) => !reorderedIds.includes(row.tagGroup.id))]
  }, [reorderedIds, storedRows])
  const rows = useMemo(() => filterTagGroupRows(orderedRows, search), [orderedRows, search])
  const isLoading = tagGroupsQuery.isLoading || tagsQuery.isLoading || languageMapsQuery.isLoading
  const isError = tagGroupsQuery.isError || tagsQuery.isError || languageMapsQuery.isError

  const tableColumns: GRTableColumn<TagGroupRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:tag_groups_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.tagGroup.name || '---'}</Typography>,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-service-management:tag_groups_type'),
        accessor: ({ row }) => t(tagGroupTypeLabelKeys[row.tagGroup.type]),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-service-management:tag_groups_color'),
        accessor: ({ row }) =>
          row.tagGroup.colorHex ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 16, height: 16, border: 1, borderColor: 'divider', bgcolor: row.tagGroup.colorHex }} />
              <Typography variant="body2" fontWeight="bold">
                {row.tagGroup.colorHex}
              </Typography>
            </Stack>
          ) : null,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 120 } },
      },
      {
        labelAccessor: t('internal-service-management:tag_groups_tags'),
        accessor: ({ row }) => row.tagNames.join(', '),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 280 } },
      },
      rowActionsColumn<TagGroupRow, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-service-management:tag_groups_edit'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-service-management:tag_groups_delete'), onClick: () => customTableProps?.onDelete(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (row) => setDialogState({ open: true, row }),
      onDelete: (row) =>
        setConfirmDialogData({
          title: t('internal-service-management:tag_groups_delete'),
          confirmText: t('internal-service-management:tag_groups_delete_confirm', { name: row.tagGroup.name }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: { action: 'delete', row },
        }),
    }),
    [t]
  )

  const changeType = useCallback(
    (nextType: TagGroupType) => {
      setReorderedIds(undefined)
      setSearch('')
      setSearchParams({ type: nextType }, { replace: true })
    },
    [setSearchParams]
  )

  const handleTypeChange = (nextType: TagGroupType | null) => {
    if (!nextType || nextType === type) return
    if (reorderedIds) {
      setConfirmDialogData({
        title: t('internal-service-management:tag_groups_discard_order'),
        confirmText: t('internal-service-management:tag_groups_discard_order_confirm'),
        actionText: t('internal-service-management:tag_groups_discard_order'),
        destructiveAction: true,
        data: { action: 'discardOrder', type: nextType },
      })
      return
    }
    changeType(nextType)
  }

  const handleSave = useCallback(
    async (tagGroup: TagGroupMutation) => {
      try {
        const stored = await saveTagGroup(tagGroup).unwrap()
        const unstored = getUnstoredTagGroupFields(tagGroup, stored)
        if (unstored.length) {
          const isCreate = !tagGroup.id
          // A created group missing its type is invisible in every type filter, so it is removed rather than left behind.
          const isRemoved =
            isCreate &&
            (await deleteTagGroup(stored.id)
              .unwrap()
              .then(
                () => true,
                () => false
              ))
          const fields = unstored.map((field) => t(tagGroupFieldLabelKeys[field])).join(', ')
          const messageKey = !isCreate
            ? 'internal-service-management:tag_groups_not_stored'
            : isRemoved
            ? 'internal-service-management:tag_groups_not_stored_created'
            : 'internal-service-management:tag_groups_not_stored_created_remove_failed'
          notifyError(t(messageKey, { fields }))
          return
        }
        notifySuccess(t('internal-service-management:tag_groups_saved', { name: tagGroup.name }))
        setDialogState({ open: false })
      } catch {
        notifyError(t('internal-service-management:tag_groups_save_failed'))
      }
    },
    [deleteTagGroup, notifyError, notifySuccess, saveTagGroup, t]
  )

  const handleSaveOrder = useCallback(async () => {
    if (!reorderedIds) return
    const saved = await run(saveTagGroupOrder(toTagGroupOrder(orderedRows)).unwrap(), {
      success: t('internal-service-management:tag_groups_order_saved'),
      error: t('internal-service-management:tag_groups_order_save_failed'),
    })
    if (saved.ok) setReorderedIds(undefined)
  }, [orderedRows, reorderedIds, run, saveTagGroupOrder, t])

  const handleConfirm = useCallback(
    async (confirmed?: ConfirmDialogData<PageConfirmData>) => {
      setConfirmDialogData(undefined)
      const data = confirmed?.data
      if (!data) return

      if (data.action === 'discardOrder') {
        changeType(data.type)
        return
      }

      await run(deleteTagGroup(data.row.tagGroup.id).unwrap(), {
        success: t('internal-service-management:tag_groups_deleted', { name: data.row.tagGroup.name }),
        error: t('internal-service-management:tag_groups_delete_failed'),
      })
    },
    [changeType, deleteTagGroup, run, t]
  )

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={type}
          onChange={(_, value) => handleTypeChange(value)}
          aria-label={t('internal-service-management:tag_groups_type')}
        >
          {tagGroupTypes.map((tagGroupType) => (
            <ToggleButton key={tagGroupType} value={tagGroupType}>
              {t(tagGroupTypeLabelKeys[tagGroupType])}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flex: 1, minWidth: 480 }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('internal-service-management:tag_groups_search')}
            inputProps={{ 'aria-label': t('internal-service-management:tag_groups_search') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
            {reorderedIds && (
              <>
                <Button onClick={() => setReorderedIds(undefined)}>{t('internal-service-management:tag_groups_reset_order')}</Button>
                <Button variant="outlined" onClick={handleSaveOrder} disabled={isSavingOrder}>
                  {t('internal-service-management:tag_groups_save_order')}
                </Button>
              </>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={() => setDialogState({ open: true })}>
              {t('internal-service-management:tag_groups_add')}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-service-management:tag_groups_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey={(row) => row.tagGroup.id}
            customProps={customProps}
            onSortRows={(sortedRows) => setReorderedIds(sortedRows.map((row) => row.tagGroup.id))}
            sortable={!search.trim()}
            disableSortTooltip={t('internal-service-management:tag_groups_reorder_disabled')}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <TagGroupEditDialog
        open={dialogState.open}
        row={dialogState.row}
        defaultType={type}
        tags={tags}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
